import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { IRole, IUser, IUserModule } from '../../../../models/settings.models';

const USER_ROLE_COLORS = [
  '#6449db', '#dc2626', '#059669', '#0891b2', '#d97706',
  '#7c3aed', '#ec4899', '#0ea5e9', '#65a30d', '#1d4ed8',
];
const EMPTY_STATE_VALUE = '--';
const DEFAULT_ROLE_COLOR = '#94a3b8';
const MODULE_TRANSLATION_PREFIX = 'user.modules.';

@Component({
  selector: 'app-user-detail-dialog',
  templateUrl: './user-detail-dialog.component.html',
  styleUrls: ['./user-detail-dialog.component.scss'],
})
export class UserDetailDialogComponent implements OnChanges {
  @Input() user!: IUser;
  @Output() toggleEnabled = new EventEmitter<IUser>();
  @Output() editUser = new EventEmitter<IUser>();

  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;

  constructor(
    private readonly dialogRef: MatDialogRef<UserDetailDialogComponent>,
    private readonly translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const changedUser = changes['user']?.currentValue as IUser | undefined;
    if (!changedUser) return;

    console.log('changedUser', changedUser);

    // Rebind to a fresh object so status/UI badges always refresh on input updates.
    this.user = { ...changedUser };
  }

  setUser(user: IUser): void {
    this.user = { ...user };
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      customConfirmButton: true,
      hideConfirmButton: true,
      cancelText: this.translateService.instant('common.close'),
      cancelButtonStyle: 'transparent',
    };
  }

  get userRole(): IRole | null {
    return this.user.roles?.[0] ?? null;
  }

  getRoleColor(role: IRole | null): string {
    return role ? USER_ROLE_COLORS[role.id! % USER_ROLE_COLORS.length] : DEFAULT_ROLE_COLOR;
  }

  getRoleLabel(roleName: string): string {
    return roleName || EMPTY_STATE_VALUE;
  }

  getImageSrc(base64: string): string {
    if (!base64) return '';
    const decoded = atob(base64);
    return decoded.trim().startsWith('<?xml')
      ? `data:image/svg+xml;base64,${base64}`
      : `data:image/*;base64,${base64}`;
  }

  getUserStatusLabel(isEnabled: boolean): string {
    return this.translateService.instant(isEnabled ? 'common.active' : 'common.inactive');
  }

  getUserToggleActionLabel(isEnabled: boolean): string {
    return this.translateService.instant(isEnabled ? 'common.deactivate' : 'common.activate');
  }

  getLastActivity(): string {
    if (!this.user.last_activity) return EMPTY_STATE_VALUE;
    try {
      const date = new Date(this.user.last_activity);
      if (isNaN(date.getTime())) return EMPTY_STATE_VALUE;
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const y = date.getFullYear();
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${d}/${m}/${y} ${h}:${min}`;
    } catch {
      return EMPTY_STATE_VALUE;
    }
  }

  getPermissionSummaryModules(): IUserModule[] {
    // Preserve the backend's canonical catalog order — do not re-sort here.
    const seen = new Set<string>();
    const entries: IUserModule[] = [];
    for (const entry of this.user.modules ?? []) {
      const module = this.normalizeModuleName(entry?.module);
      if (!module || seen.has(module)) continue;
      seen.add(module);
      entries.push({ module, own_data: entry?.own_data === true });
    }
    return entries;
  }

  getModuleLabel(moduleName: string): string {
    const normalized = this.normalizeModuleName(moduleName);
    const key = `${MODULE_TRANSLATION_PREFIX}${normalized}`;
    const translated = this.translateService.instant(key);
    if (translated !== key) return translated;

    return normalized
      .split('_')
      .map(word => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ''))
      .join(' ')
      .trim();
  }

  close(): void {
    this.dialogRef.close();
  }

  onEditUser(): void {
    this.editUser.emit(this.user);
  }

  onToggleEnabled(): void {
    this.toggleEnabled.emit(this.user);
  }

  private normalizeModuleName(moduleName: string): string {
    return (moduleName ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_');
  }
}
